// Wire codes of the binder page layout API (mtg-api plan "Binder page scan",
// app/routes/binder_layouts.py; wire fixtures in
// src/api/__fixtures__/binder_layout_wire/). Every literal here is a machine
// code the server sends or accepts, never text a user reads; the copy lives
// in the `collection` catalog under binders.arranged.*.

/** URL segments under /collections/{id}/binders/{binder_id}. */
export const LAYOUT_SEG = {
  layout: '/layout',
  pockets: '/pockets',
} as const

/** Query keys of GET and DELETE /layout. */
export const LAYOUT_QUERY = {
  pages: 'pages',
  expectedGeneration: 'expected_generation',
  expectedLayoutRev: 'expected_layout_rev',
  opKey: 'op_key',
} as const

/** Request and response headers of the revalidation. */
export const LAYOUT_HTTP = {
  ifNoneMatch: 'If-None-Match',
  etag: 'ETag',
  notModified: 304,
} as const

/** A pocket's state after the server's reconciliation. */
export const POCKET_STATES = ['held', 'missing'] as const
export type PocketState = typeof POCKET_STATES[number]
export const POCKET_STATE = { held: 'held', missing: 'missing' } as const

/** PATCH /pockets op kinds. */
export const POCKET_OP = { move: 'move', swap: 'swap', clear: 'clear' } as const
export type PocketOpKind = typeof POCKET_OP[keyof typeof POCKET_OP]

/** `detail` codes of the layout routes the client branches on. */
export const LAYOUT_DETAIL = {
  /** 404: the binder has no layout (never had one, or it was removed elsewhere). */
  layoutNone: 'layout_none',
  /** 409: the layout moved on since it was read (or was deleted and recreated). */
  layoutRevMismatch: 'layout_rev_mismatch',
  /** 422: smart binders and deck boxes have no layout. */
  binderNotManual: 'binder_not_manual',
  pocketEmpty: 'pocket_empty',
  pocketOccupied: 'pocket_occupied',
  pocketOutOfRange: 'pocket_out_of_range',
  repeatedCoordinate: 'repeated_coordinate',
  pageLimit: 'page_limit',
  badOp: 'bad_op',
} as const

/** The server's grid bounds (cols and rows each 3..5; 4x3 is allowed). */
export const LAYOUT_GRID_MIN = 3
export const LAYOUT_GRID_MAX = 5
/** The server's page cap (MAX_PAGES). */
export const LAYOUT_MAX_PAGES = 200

/** TanStack root of the layout reads (its own root: the data is not a binder list). */
export const QK_BINDER_LAYOUT = 'copies-binder-layout'

/**
 * Pages per layout read: the view reads the chunk that holds its page
 * (pages a..a+9), so a page turn inside the chunk never waits and the
 * response stays small. `unplaced` and `page_count` are binder-wide in
 * every chunk.
 */
export const LAYOUT_CHUNK_PAGES = 10

/** Revalidation while a layout binder is on screen and the tab is visible. */
export const LAYOUT_POLL_MS = 60_000

/** The binder settings menu's layout entries (React keys). */
export const LAYOUT_MENU = {
  arrange: 'layout-arrange',
  addPage: 'layout-add-page',
  trimPages: 'layout-trim-pages',
  removeLayout: 'layout-remove',
} as const

/**
 * localStorage: the binders this device last saw WITH a layout
 * (owner|collection|binder ids). Only those wait for the layout read with a
 * skeleton; every other manual binder renders at once while it is asked.
 */
export const LAYOUT_KNOWN_STORAGE = 'cb.binderLayout.known.v1'
/** The most binders remembered (the oldest drop off). */
export const LAYOUT_KNOWN_MAX = 200
